import styled from '@emotion/styled';
import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ReactComponent as People } from '../ui/icons/categories/accueil public.svg';
import { ReactComponent as Dress } from '../ui/icons/categories/achats.svg';
import { ReactComponent as Building } from '../ui/icons/categories/batiment.svg';
import { ReactComponent as BusStop } from '../ui/icons/categories/busstop.svg';
import { ReactComponent as Catering } from '../ui/icons/categories/catering.svg';
import { ReactComponent as CloudIcon } from '../ui/icons/categories/cloud.svg';
import { ReactComponent as Computer } from '../ui/icons/categories/computer.svg';
import { ReactComponent as ConsoleIcon } from '../ui/icons/categories/console.svg';
import { ReactComponent as ExplanationIcon } from '../ui/icons/categories/explanation.svg';
import { ReactComponent as Furniture } from '../ui/icons/categories/furniture.svg';
import { ReactComponent as GoodsIcon } from '../ui/icons/categories/goods.svg';
import { ReactComponent as Hotel } from '../ui/icons/categories/hotel.svg';
import { ReactComponent as Identity } from '../ui/icons/categories/infos générales.svg';
import { ReactComponent as Truck } from '../ui/icons/categories/marchandise.svg';
import { ReactComponent as Bike } from '../ui/icons/categories/mobilité.svg';
import { ReactComponent as Outdoor } from '../ui/icons/categories/outdoor.svg';
import { ReactComponent as Production } from '../ui/icons/categories/production.svg';
import { ReactComponent as ServersIcon } from '../ui/icons/categories/servers.svg';
import { ReactComponent as SmartphoneIcon } from '../ui/icons/categories/smartphone.svg';
import { ReactComponent as Social } from '../ui/icons/categories/social.svg';
import { ReactComponent as Trash } from '../ui/icons/categories/trash.svg';
import { ReactComponent as TripIcon } from '../ui/icons/categories/trip.svg';
import { ReactComponent as Energy } from '../ui/icons/categories/énergie.svg';
import TeamsIcon from '../ui/icons/categories/team.png';
import PartnershipsIcon from '../ui/icons/categories/partnerships.png';
import AssetsIcon from '../ui/icons/categories/assets.png';
import DiffusionIcon from '../ui/icons/categories/diffusion.png';
import FoodIcon from '../ui/icons/categories/food.png';
import TransportIcon from '../ui/icons/categories/transport.png';
import EnergyWaterIcon from '../ui/icons/categories/energy-water.png';
import ItIcon from '../ui/icons/categories/it.png';
import WasteIcon from '../ui/icons/categories/waste.png';
import InfoOutlinedIcon from '../ui/icons/categories/general-infos.png';
import { useProject } from './context/useProject';
import keyify from './parameters/keyify';

const Container = styled.div`
  width: 100%;
  padding: 16px;
`;

const CategoriesWrapper = styled.div`
  position: relative;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  height: 62px;
  gap: 16px;
  flex-wrap: wrap;
`;

const MoreMenuContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`;

const MoreButton = styled(IconButton)`
  height: 32px;
  width: 32px;
  padding: 16px;
`;

interface CategoryInformation {
  name: string;
  color: string;
  icon: string;
}

const contrastColor = (backgroundColor: string) => {
  try {
    return ['black', 'white'][
      ~~(
        [1, 3, 5]
          .map((p) => parseInt(backgroundColor.substr(p, 2), 16))
          .reduce((r, v, i) => [0.299, 0.587, 0.114][i] * v + r, 0) < 128
      )
    ];
  } catch (e) {
    return 'black';
  }
};

interface CategoryInformation {
  name: string;
  color: string;
  icon: string;
}

const MoreMenuItem = ({
  label,
  color,
  scrollHandle,
  handleClose,
}: {
  label: string;
  color: string;
  scrollHandle: string;
  handleClose: () => void;
}) => {
  const handleClick = () => {
    handleClose();
    setTimeout(() => {
      document?.getElementById(scrollHandle)?.scrollIntoView();
    });
  };

  return (
    <MenuItem
      onClick={handleClick}
      sx={{
        '&:hover': {
          backgroundColor: color,
          color: contrastColor(color),
          svg: {
            fill: contrastColor(color),
          },
        },
      }}
    >
      <ListItemText primary={label} />
    </MenuItem>
  );
};

const MoreMenu = ({
  categoryInformation,
}: {
  categoryInformation: CategoryInformation[];
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuItems, setMenuItems] = useState<CategoryInformation[]>([]);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    const allButtons = Array.from(
      document.querySelectorAll('.nav-button'),
    ) as HTMLElement[];
    const minTop = allButtons.reduce((minTop, button) => {
      const { top } = button.getBoundingClientRect();
      return top < minTop ? top : minTop;
    }, Number.MAX_VALUE);

    const secondLineButtons = allButtons.filter((button) => {
      const { top } = button.getBoundingClientRect();
      return top > minTop;
    });
    setMenuItems(
      secondLineButtons.map((button) => {
        const index = [...(button.parentElement?.children || [])].indexOf(
          button,
        );
        return {
          name: button.textContent || '',
          color: categoryInformation[index]?.color || '#ffffff',
          icon: '',
        };
      }),
    );
  }, [categoryInformation]);

  const handleClose = () => {
    setAnchorEl(null);
  };
  return menuItems.length ? (
    <MoreMenuContainer>
      <MoreButton
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <MoreHorizIcon />
      </MoreButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {menuItems.map(({ name, color }) => (
          <MoreMenuItem
            key={name}
            label={name}
            color={color}
            scrollHandle={`${keyify(name)}-0`}
            handleClose={handleClose}
          />
        ))}
      </Menu>
    </MoreMenuContainer>
  ) : null;
};

const Nav = () => {
  const { projectId } = useParams();
  const { project } = useProject(projectId);
  const { t } = useTranslation(['project']);

  if (!project) return null;

  const firstCategoriesInformation = project.sectors.map(
    (sector) => sector.information,
  );

  return (
    <Container>
      <CategoriesWrapper className="categories_wrapper">
        {firstCategoriesInformation.map(({ name, color, icon }, index) => {
          const translationKey = `model:${keyify(
            project.model.name,
          )}.section.level-0.${keyify(name || '')}`;
          return (
            <NavButton
              key={index}
              scrollHandle={`${keyify(name)}-0`}
              label={t(translationKey)}
              color={color}
              icon={categoryIcons[icon] || <Identity />}
            />
          );
        })}
        <MoreMenu categoryInformation={firstCategoriesInformation} />
      </CategoriesWrapper>
    </Container>
  );
};

const categoryIcons: { [key: string]: ReactElement } = {
  explanation: <ExplanationIcon />,
  identity: <img src={InfoOutlinedIcon} alt="identity" />,
  console: <ConsoleIcon />,
  mobile: <SmartphoneIcon />,
  cloud: <CloudIcon />,
  people: <People />,
  energy: <Energy />,
  assets: <img src={AssetsIcon} alt="assets" />,
  trip: <TripIcon />,
  projector: <Production />,
  building: <Building />,
  bike: <Bike />,
  hotel: <Hotel />,
  food: <img src={FoodIcon} alt="food" />,
  dress: <Dress />,
  computer: <Computer />,
  truck: <Truck />,
  social: <Social />,
  catering: <Catering />,
  outdoor: <Outdoor />,
  furniture: <Furniture />,
  trash: <Trash />,
  busstop: <BusStop />,
  'it-purchase': <ServersIcon />,
  'product-service-purchase': <GoodsIcon />,
  waste: <img src={WasteIcon} alt="waste" />,
  teams: <img src={TeamsIcon} alt="teams" />,
  partnerships: <img src={PartnershipsIcon} alt="partnerships" />,
  transport: <img src={TransportIcon} alt="transport" />,
  'energy-water': <img src={EnergyWaterIcon} alt="energy-water" />,
  diffusion: <img src={DiffusionIcon} alt="diffusion" />,
  it: <img src={ItIcon} alt="it" />,
};

interface NavButtonWrapperProps {
  hoverColor?: string;
}

const NavButtonWrapper = styled.div<NavButtonWrapperProps>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  min-width: 80px;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 62px;
  transition: background-color 0.2s;
  :hover,
  &.selected {
    background-color: ${({ hoverColor }) => hoverColor};
    color: ${({ hoverColor }) => contrastColor(hoverColor!)};
    svg:not([fill='none']) {
      fill: ${({ hoverColor }) => contrastColor(hoverColor!)};
    }
    svg[fill='none'] {
      stroke: ${({ hoverColor }) => contrastColor(hoverColor!)};
    }
  }
  p {
    text-align: center;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-self: center;
  height: 30px;
  max-width: 30px;
  img {
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
  }
`;

const NavButton = ({
  label,
  icon,
  color,
  scrollHandle,
}: {
  label: string;
  icon?: ReactNode | string;
  color: string;
  scrollHandle?: string;
}) => {
  const handleButtonClicked = () => {
    if (scrollHandle) {
      document?.getElementById(scrollHandle)?.scrollIntoView();
    }
  };
  return (
    <NavButtonWrapper
      className="nav-button"
      hoverColor={color}
      onClick={handleButtonClicked}
      title={label}
    >
      {icon && typeof icon === 'string' ? (
        <IconContainer>
          <img src={icon} alt={label} />
        </IconContainer>
      ) : (
        <IconContainer>{icon}</IconContainer>
      )}
      <p className="hxr">{label}</p>
    </NavButtonWrapper>
  );
};

export default Nav;
